import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useGetPublicSettingsQuery, useGetSettingsByPathLazyQuery, SettingDataType, GetSettingsByPathQuery } from 'generated/graphql';

/** Partial setting type from the settingsByPath query (excludes createdAt) */
type PartialSetting = GetSettingsByPathQuery['settingsByPath'][number];

const CACHE_KEY = 'recore:settings:public';
const CACHE_VERSION = '1.0.0';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

interface CachedSettings {
  version: string;
  timestamp: number;
  data: Record<string, { value: string; dataType: SettingDataType }>;
}

interface SettingsContextValue {
  isLoading: boolean;
  isReady: boolean;

  /** Get a setting value, returns undefined if not found */
  getSetting<T = string>(path: string): T | undefined;

  /** Get a setting value with a default fallback */
  getSetting<T = string>(path: string, defaultValue: T): T;

  /** Check if a boolean setting is true */
  isEnabled(path: string): boolean;

  /** Lazy load settings by path prefix */
  loadSettingsByPath(prefix: string): Promise<PartialSetting[]>;

  /** Force refresh settings from server */
  refreshSettings(): Promise<void>;
}

const SettingsContext = createContext<SettingsContextValue | null>(null);

export function SettingsProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<Record<string, { value: string; dataType: SettingDataType }>>({});
  const [isReady, setIsReady] = useState(false);

  const { loading, data, refetch } = useGetPublicSettingsQuery({
    skip: isCacheValid(),
    fetchPolicy: 'network-only',
  });

  const [fetchByPath] = useGetSettingsByPathLazyQuery();

  // Load from cache on mount
  useEffect(() => {
    const cached = loadFromCache();
    if (cached) {
      setSettings(cached);
      setIsReady(true);
    }
  }, []);

  // Update from server response
  useEffect(() => {
    if (data?.publicSettings) {
      const newSettings: Record<string, { value: string; dataType: SettingDataType }> = {};
      for (const setting of data.publicSettings) {
        newSettings[setting.path] = { value: setting.value, dataType: setting.dataType };
      }
      setSettings(newSettings);
      saveToCache(newSettings);
      setIsReady(true);
    }
  }, [data]);

  const getSetting = useCallback(<T = string>(path: string, defaultValue?: T): T | undefined => {
    const setting = settings[path];
    if (!setting) return defaultValue;

    return parseValue<T>(setting.value, setting.dataType) ?? defaultValue;
  }, [settings]);

  const isEnabled = useCallback((path: string): boolean => {
    return getSetting<boolean>(path, false) === true;
  }, [getSetting]);

  const loadSettingsByPath = useCallback(async (prefix: string): Promise<PartialSetting[]> => {
    const result = await fetchByPath({ variables: { pathPrefix: prefix } });
    return result.data?.settingsByPath ?? [];
  }, [fetchByPath]);

  const refreshSettings = useCallback(async (): Promise<void> => {
    clearCache();
    await refetch();
  }, [refetch]);

  const value = useMemo<SettingsContextValue>(() => ({
    isLoading: loading,
    isReady,
    getSetting,
    isEnabled,
    loadSettingsByPath,
    refreshSettings,
  }), [loading, isReady, getSetting, isEnabled, loadSettingsByPath, refreshSettings]);

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings(): SettingsContextValue {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}

// Helper functions
function isCacheValid(): boolean {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return false;
    const cached: CachedSettings = JSON.parse(raw);
    if (cached.version !== CACHE_VERSION) return false;
    if (Date.now() - cached.timestamp > CACHE_TTL_MS) return false;
    return true;
  } catch {
    return false;
  }
}

function loadFromCache(): Record<string, { value: string; dataType: SettingDataType }> | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const cached: CachedSettings = JSON.parse(raw);
    if (cached.version !== CACHE_VERSION) return null;
    if (Date.now() - cached.timestamp > CACHE_TTL_MS) return null;
    return cached.data;
  } catch {
    return null;
  }
}

function saveToCache(data: Record<string, { value: string; dataType: SettingDataType }>): void {
  const cached: CachedSettings = {
    version: CACHE_VERSION,
    timestamp: Date.now(),
    data,
  };
  localStorage.setItem(CACHE_KEY, JSON.stringify(cached));
}

function clearCache(): void {
  localStorage.removeItem(CACHE_KEY);
}

function parseValue<T>(value: string, dataType: SettingDataType): T | undefined {
  try {
    switch (dataType) {
      case SettingDataType.Boolean:
        return (value.toLowerCase() === 'true') as unknown as T;
      case SettingDataType.Integer:
        return parseInt(value, 10) as unknown as T;
      case SettingDataType.Decimal:
        return parseFloat(value) as unknown as T;
      case SettingDataType.DateTime:
        return new Date(value) as unknown as T;
      case SettingDataType.Json:
      case SettingDataType.StringList:
        return JSON.parse(value) as T;
      case SettingDataType.String:
      default:
        return value as unknown as T;
    }
  } catch {
    return undefined;
  }
}
